import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent, type ReactNode } from 'react';
import { ApiError, api, upgradeOf } from '../api/client.ts';
import type { MemberRole, Person, PlanMember, PlanPeople, PlanSummary, UpgradeRequired } from '../api/types.ts';
import { t } from '../i18n/index.ts';
import { personName, useStore } from '../state/store.tsx';
import { useToast } from '../state/toast.tsx';
import { Icon } from './Icon.tsx';
import { planErrorMessage, RoleBadge } from './PlanSwitcher.tsx';
import { Field, Modal, Segmented } from './ui.tsx';
import { UpgradeNotice } from './UpgradePrompt.tsx';

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function emailError(raw: string): string | null {
  const email = raw.trim();
  if (!email) return t('people.error.emailRequired');
  if (!EMAIL.test(email)) return t('people.error.emailInvalid');
  return null;
}

function peopleErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 429) return t('people.error.rateLimited');
    if (error.status === 403) return t('people.error.forbidden');
  }
  return planErrorMessage(error);
}

const initial = (person: Person) => personName(person).charAt(0).toUpperCase();

export const sharedWithLabel = (n: number) => (n === 1 ? t('people.sharedWith.one') : t('people.sharedWith.many', { n }));

/**
 * Who is on a plan. The owner adds people by email, changes their role and removes them; a member sees
 * who else is on it and can leave. Opened from each plan on the Plans screen.
 */
export function PeopleDialog({ plan, onClose, onLeft }: { plan: PlanSummary; onClose: () => void; onLeft?: () => void }) {
  const { me, refreshPlans } = useStore();
  const [people, setPeople] = useState<PlanPeople | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState('');
  const introId = useId();
  const owner = plan.role === 'owner';

  const load = useCallback(async () => {
    setLoadError(false);
    try {
      setPeople(await api.people(plan.id));
    } catch {
      setLoadError(true);
    }
  }, [plan.id]);
  useEffect(() => {
    void load();
  }, [load]);

  const changed = (next: PlanPeople, message: string) => {
    setPeople(next);
    setStatus(message);
    void refreshPlans();
  };

  const myId = me?.user.id;
  return (
    <Modal
      title={t('people.title', { name: plan.name })}
      onClose={onClose}
      describedBy={introId}
      footer={<button type="button" className="button button--ghost" onClick={onClose}>{t('people.done')}</button>}
    >
      <div className="people-dialog">
        <p className="modal__text" id={introId}>
          {owner ? t('people.intro.owner') : t('people.intro.member', { owner: people ? personName(people.owner) : t('plans.role.owner') })}
        </p>
        {owner && <AddPerson plan={plan} people={people} onAdded={(member) => people && changed({ ...people, members: [...people.members, member] }, t(`people.added.${member.role}`, { name: personName(member) }))} />}
        {loadError ? (
          <div className="people-load-error" role="alert">
            <p className="form-error"><Icon name="alert" size={15} />{t('people.error.load')}</p>
            <button type="button" className="button button--sm button--ghost" onClick={() => void load()}>{t('common.retry')}</button>
          </div>
        ) : !people ? (
          <p className="muted people-loading" role="status">{t('people.loading')}</p>
        ) : (
          <PeopleList plan={plan} people={people} myId={myId} onChange={changed} />
        )}
        {!owner && people && <LeavePlan plan={plan} ownerName={personName(people.owner)} onLeft={() => { onClose(); onLeft?.(); }} />}
        <p className="sr-only" role="status">{status}</p>
      </div>
    </Modal>
  );
}

function PeopleList({ plan, people, myId, onChange }: {
  plan: PlanSummary;
  people: PlanPeople;
  myId: string | undefined;
  onChange: (next: PlanPeople, message: string) => void;
}) {
  const headingId = useId();
  const heading = useRef<HTMLHeadingElement>(null);
  const owner = plan.role === 'owner';
  const count = people.members.length;
  const replace = (member: PlanMember, message: string) =>
    onChange({ ...people, members: people.members.map((m) => (m.userId === member.userId ? member : m)) }, message);
  const remove = (member: PlanMember) => {
    onChange({ ...people, members: people.members.filter((m) => m.userId !== member.userId) }, t('people.removed', { name: personName(member) }));
    heading.current?.focus();
  };
  return (
    <section className="people" aria-labelledby={headingId}>
      <h3 className="people__head" id={headingId} ref={heading} tabIndex={-1}>
        <span className="eyebrow">{t('people.listTitle')}</span>
        <span className="fine-print">{count === 0 ? t('people.onlyOwner') : sharedWithLabel(count)}</span>
      </h3>
      <ul className="people__list">
        <PersonShell person={people.owner} you={people.owner.userId === myId} badge={<RoleBadge role="owner" />} />
        {people.members.map((member) =>
          owner ? (
            <ManagedMember key={member.userId} planId={plan.id} member={member} onUpdated={replace} onRemoved={remove} />
          ) : (
            <PersonShell
              key={member.userId}
              person={member}
              you={member.userId === myId}
              badge={<RoleBadge role={member.role} />}
              note={member.role === 'viewer' && member.canRunScenarios ? t('people.canWhatIf') : null}
            />
          ),
        )}
      </ul>
      {owner && count === 0 && <p className="people__empty">{t('people.empty')}</p>}
    </section>
  );
}

function PersonShell({ person, you, badge, note, children, className = '' }: {
  person: Person;
  you: boolean;
  badge?: ReactNode;
  note?: string | null;
  children?: ReactNode;
  className?: string;
}) {
  const name = personName(person);
  return (
    <li className={`person ${className}`}>
      <div className="person__main">
        <span className={`person__avatar ${you ? 'person__avatar--you' : ''}`} aria-hidden="true">{initial(person)}</span>
        <span className="person__text">
          <span className="person__name">
            {you ? t('people.you') : name}
            {badge}
          </span>
          <span className="person__email">{person.email}</span>
          {note && <span className="person__note"><Icon name="sparkle" size={12} />{note}</span>}
        </span>
      </div>
      {children}
    </li>
  );
}

function ManagedMember({ planId, member, onUpdated, onRemoved }: {
  planId: string;
  member: PlanMember;
  onUpdated: (member: PlanMember, message: string) => void;
  onRemoved: (member: PlanMember) => void;
}) {
  const name = personName(member);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState<UpgradeRequired | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [pending, setPending] = useState<{ role?: MemberRole; canRunScenarios?: boolean } | null>(null);
  const shown = { ...member, ...pending };
  const removeButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const confirmId = useId();
  const whatIfId = useId();

  useEffect(() => {
    if (confirming) cancelButton.current?.focus();
  }, [confirming]);

  const update = async (patch: { role?: MemberRole; canRunScenarios?: boolean }) => {
    setBusy(true);
    setError(null);
    setUpgrade(null);
    setPending(patch);
    try {
      const { member: next } = await api.updateMember(planId, member.userId, patch);
      const message = patch.role ? t(`people.updated.${next.role}`, { name }) : next.canRunScenarios ? t('people.whatIfs.on', { name }) : t('people.whatIfs.off', { name });
      onUpdated(next, message);
    } catch (err) {
      const needed = upgradeOf(err);
      if (needed) setUpgrade(needed);
      else setError(peopleErrorMessage(err));
    } finally {
      setPending(null);
      setBusy(false);
    }
  };
  const cancel = () => {
    setConfirming(false);
    removeButton.current?.focus();
  };
  const remove = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.removeMember(planId, member.userId);
      onRemoved(member);
    } catch (err) {
      setError(peopleErrorMessage(err));
      setBusy(false);
    }
  };
  const escape = (e: KeyboardEvent) => {
    if (e.key !== 'Escape') return;
    e.stopPropagation();
    cancel();
  };

  return (
    <PersonShell person={member} you={false} className={confirming ? 'person--confirming' : ''}>
      <div className="person__controls">
        <select
          className="input person__role"
          aria-label={t('people.roleFor', { name })}
          value={shown.role}
          disabled={busy}
          onChange={(e) => void update({ role: e.target.value as MemberRole })}
        >
          <option value="viewer">{t('plans.role.viewer')}</option>
          <option value="editor">{t('plans.role.editor')}</option>
        </select>
        <button
          ref={removeButton}
          type="button"
          className="icon-button person__remove"
          aria-label={t('people.removeLabel', { name })}
          aria-expanded={confirming}
          aria-controls={confirming ? confirmId : undefined}
          disabled={busy && !confirming}
          onClick={() => setConfirming(true)}
        >
          <Icon name="trash" size={15} />
        </button>
      </div>
      {shown.role === 'viewer' && (
        <label className="toggle-label person__whatif" htmlFor={whatIfId}>
          <input
            id={whatIfId}
            type="checkbox"
            role="switch"
            className="toggle"
            checked={shown.canRunScenarios}
            disabled={busy}
            aria-label={t('people.whatIfsFor', { name })}
            onChange={(e) => void update({ canRunScenarios: e.target.checked })}
          />
          <span aria-hidden="true">{t('people.whatIfs')}</span>
        </label>
      )}
      {confirming && (
        <div className="person__confirm" id={confirmId} role="group" aria-label={t('people.removeLabel', { name })} onKeyDown={escape}>
          <p>{t('people.remove.body', { name })}</p>
          <div className="person__confirm-actions">
            <button ref={cancelButton} type="button" className="button button--sm button--ghost" onClick={cancel}>{t('common.cancel')}</button>
            <button type="button" className="button button--sm button--warn" disabled={busy} onClick={() => void remove()}>
              {t('people.remove.confirm', { name })}
            </button>
          </div>
        </div>
      )}
      {error && <p className="form-error person__error" role="alert"><Icon name="alert" size={15} />{error}</p>}
      {upgrade && <UpgradeNotice upgrade={upgrade} onDismiss={() => setUpgrade(null)} className="person__upgrade" />}
    </PersonShell>
  );
}

function AddPerson({ plan, people, onAdded }: { plan: PlanSummary; people: PlanPeople | null; onAdded: (member: PlanMember) => void }) {
  const id = useId();
  const input = useRef<HTMLInputElement>(null);
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<MemberRole>('viewer');
  const [whatIfs, setWhatIfs] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [upgrade, setUpgrade] = useState<UpgradeRequired | null>(null);
  const fieldError = touched ? emailError(email) : null;

  const knownConflict = (address: string): string | null => {
    if (!people) return null;
    if (people.owner.email.toLowerCase() === address) return t('people.error.self');
    const existing = people.members.find((m) => m.email.toLowerCase() === address);
    return existing ? t('people.error.already', { name: personName(existing) }) : null;
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setError(null);
    setUpgrade(null);
    if (emailError(email)) {
      input.current?.focus();
      return;
    }
    const address = email.trim().toLowerCase();
    const conflict = knownConflict(address);
    if (conflict) {
      setError(conflict);
      return;
    }
    setBusy(true);
    try {
      const { member } = await api.addMember(plan.id, { email: address, role, ...(role === 'viewer' ? { canRunScenarios: whatIfs } : {}) });
      onAdded(member);
      setEmail('');
      setTouched(false);
      setWhatIfs(false);
      input.current?.focus();
    } catch (err) {
      const needed = upgradeOf(err);
      if (needed) setUpgrade(needed);
      else if (err instanceof ApiError && err.status === 404) setError(t('people.error.notFound'));
      else if (err instanceof ApiError && err.status === 409) setError(knownConflict(address) ?? t('people.error.conflict'));
      else setError(peopleErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="people-add" onSubmit={(e) => void submit(e)} noValidate aria-labelledby={`${id}-title`}>
      <h3 className="eyebrow" id={`${id}-title`}>{t('people.add.title')}</h3>
      <Field label={t('people.add.email')} htmlFor={`${id}-email`} hint={t('people.add.emailHint')} error={fieldError}>
        <input
          ref={input}
          id={`${id}-email`}
          className="input"
          type="email"
          inputMode="email"
          autoComplete="off"
          autoCapitalize="off"
          spellCheck={false}
          placeholder={t('people.add.placeholder')}
          value={email}
          aria-invalid={fieldError ? true : undefined}
          onChange={(e) => { setEmail(e.target.value); setError(null); }}
          onBlur={() => email && setTouched(true)}
        />
      </Field>
      <div className="people-add__role">
        <span className="field__label" aria-hidden="true">{t('people.add.role')}</span>
        <Segmented
          label={t('people.add.role')}
          variant="pill"
          options={[{ value: 'viewer', label: t('plans.role.viewer') }, { value: 'editor', label: t('plans.role.editor') }]}
          value={role}
          onChange={setRole}
        />
        <span className="field__hint">{role === 'viewer' ? t('people.role.viewerHint') : t('people.role.editorHint')}</span>
      </div>
      {role === 'viewer' && (
        <label className="toggle-label people-add__whatif">
          <input type="checkbox" role="switch" className="toggle" checked={whatIfs} onChange={(e) => setWhatIfs(e.target.checked)} />
          <span className="people-add__whatif-text">
            <b>{t('people.add.whatIfs')}</b>
            <span>{t('people.add.whatIfsHint')}</span>
          </span>
        </label>
      )}
      {error && <p className="form-error" role="alert"><Icon name="alert" size={15} />{error}</p>}
      {upgrade && <UpgradeNotice upgrade={upgrade} onDismiss={() => setUpgrade(null)} />}
      <button type="submit" className="button button--primary people-add__submit" disabled={busy}>
        <Icon name="plus" size={16} strokeWidth={2.4} />{busy ? t('people.add.adding') : t('people.add.submit')}
      </button>
    </form>
  );
}

function LeavePlan({ plan, ownerName, onLeft }: { plan: PlanSummary; ownerName: string; onLeft: () => void }) {
  const { leavePlan } = useStore();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const leaveButton = useRef<HTMLButtonElement>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const confirmId = useId();
  useEffect(() => {
    if (confirming) cancelButton.current?.focus();
  }, [confirming]);
  const cancel = () => {
    setConfirming(false);
    setError(null);
    queueMicrotask(() => leaveButton.current?.focus());
  };
  const leave = async () => {
    setBusy(true);
    setError(null);
    try {
      await leavePlan(plan.id);
      toast.push({ tone: 'success', message: t('people.left', { name: plan.name }) });
      onLeft();
    } catch (err) {
      setError(peopleErrorMessage(err));
      setBusy(false);
    }
  };
  return (
    <div className="people-leave">
      {confirming ? (
        <div
          className="person__confirm"
          id={confirmId}
          role="group"
          aria-label={t('people.leave')}
          onKeyDown={(e) => {
            if (e.key !== 'Escape') return;
            e.stopPropagation();
            cancel();
          }}
        >
          <p>{t('people.leave.body', { name: plan.name, owner: ownerName })}</p>
          <div className="person__confirm-actions">
            <button ref={cancelButton} type="button" className="button button--sm button--ghost" onClick={cancel}>{t('common.cancel')}</button>
            <button type="button" className="button button--sm button--warn" disabled={busy} onClick={() => void leave()}>{t('people.leave.confirm')}</button>
          </div>
        </div>
      ) : (
        <button ref={leaveButton} type="button" className="button button--sm button--ghost people-leave__button" onClick={() => setConfirming(true)}>
          <Icon name="logout" size={15} />{t('people.leave')}
        </button>
      )}
      {error && <p className="form-error" role="alert"><Icon name="alert" size={15} />{error}</p>}
    </div>
  );
}
